import { Box, Button, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import AccessTimedOutIcon from '../../assets/icons/AccessTimedOut.svg';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 300000;

import MicrosoftIcon from '../../assets/icons/Microsoft.svg';
import TrashIcon from '../../assets/icons/Trash.svg';
import Connected from '../../assets/icons/connected.svg';
import MoreIcon from '../../assets/icons/more.svg';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { MicrosoftService } from '../../service/MicrosoftService';
import { useStoreMicrosoftData } from '../../store/storeMicrosoftData';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import LogoutModal from '../Integrations/LogoutModal';

enum ConnectionState {
  NotConnected = 'Not Connected',
  JustConnected = 'Just Connected',
  Connected = 'Connected',
  AccessTimedOut = 'Access Timed Out',
}

const MicrosoftIntegration = () => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const { isMicrosoftConnected, setIsMicrosoftConnected } = useStoreMicrosoftData();
  const [installUrl, setInstallUrl] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectedEmail, setConnectedEmail] = useState('');
  const [showLogoutBox, setShowLogoutBox] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.NotConnected,
  );
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isMicrosoftConnected) setConnectionState(ConnectionState.Connected);
  }, [isMicrosoftConnected]);
  
  useEffect(() => {
    const getConnectedEmailHandler = async () => {
      if (
        connectionState === ConnectionState.Connected ||
        connectionState === ConnectionState.JustConnected
      ) {
        try {
          const response = await MicrosoftService.getProfile();
          const d = response.data as {
            mail?: string;
            userPrincipalName?: string;
            displayName?: string;
          };
          setConnectedEmail(
            d?.mail ?? d?.userPrincipalName ?? d?.displayName ?? '',
          );
        } catch (error) {
          setNotification({
            type: NotificationType.Error,
            message: 'Something went wrong fetching Microsoft profile',
          });
        }
      } else {
        try {
          const response = await MicrosoftService.getInstallUrl();
          setInstallUrl(response.installUrl);
        } catch (error) {
          setNotification({
            type: NotificationType.Error,
            message: 'Something went wrong fetching Microsoft profile',
          });
        }
      }
    };

    getConnectedEmailHandler();
  }, [connectionState]);

  const logoutHandler = async () => {
    try {
      await MicrosoftService.disconnect();
    } catch {
      // best effort
    }
    setConnectionState(ConnectionState.NotConnected);
    setIsMicrosoftConnected(false);
    setShowLogoutBox(false);
    setShowLogoutModal(false);
  };

  const connectionHandler = useCallback(async () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

    try {
      const response = await MicrosoftService.getInstallUrl();
      const url = response.installUrl;
      if (!url) {
        setNotification({
          type: NotificationType.Error,
          message: 'Could not load Microsoft connection URL.',
        });
        return;
      }
      window.open(url, 'microsoftIntegration', 'popup,width=500,height=600');
      setIsConnecting(true);

      pollTimeoutRef.current = setTimeout(() => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        pollTimeoutRef.current = null;
        setIsConnecting(false);
        setNotification({
          type: NotificationType.Error,
          message: 'Connection timed out. Please try again.',
        });
      }, POLL_TIMEOUT_MS);

      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusRes = await MicrosoftService.getLinkStatus();
          const status = statusRes.data?.status;
          
          if (status === 'completed') {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            if (pollTimeoutRef.current) {
              clearTimeout(pollTimeoutRef.current);
              pollTimeoutRef.current = null;
            }
            setIsMicrosoftConnected(true);
            setConnectionState(ConnectionState.JustConnected);
            setIsConnecting(false);
          }
        } catch {
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      setNotification({
        type: NotificationType.Error,
        message: getAPIErrorMessage(err as Error),
      });
    }
  }, [setIsMicrosoftConnected, setNotification]);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const renderLogoutBox = () =>
    showLogoutBox ? (
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '-105px',
          boxShadow: '2px 4px 35px 0px rgba(0, 0, 0, 0.25)',
          background: 'rgba(255, 255, 255, 1)',
          padding: '20px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
        }}
        onClick={() => setShowLogoutModal(true)}
      >
        <TrashIcon />
        <Typography>Log out</Typography>
      </Box>
    ) : null;

  const renderConnectionState = () => {
    const cardSx = {
      width: 'min-content',
      minWidth: '250px',
      border: '1px solid rgba(196, 207, 255, 1)',
      padding: '24px',
      borderRadius: '8px',
      position: 'relative' as const,
    };
    const titleSx = {
      color: 'rgba(71, 84, 103, 1)',
      fontWeight: 600,
      fontSize: '12px',
      lineHeight: '19.2px',
      letterSpacing: '0.15px',
    };
    const subtitleSx = {
      fontSize: '12px',
      lineHeight: '19.2px',
      letterSpacing: '0.15px',
      color: 'rgba(102, 112, 133, 1)',
    };

    if (connectionState === ConnectionState.NotConnected) {
      return (
        <Box sx={cardSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderBottom: '.5px solid rgba(234, 236, 240, 1)',
            }}
          >
            <MicrosoftIcon />
            <Typography sx={titleSx}>Microsoft Account</Typography>
          </Box>
          <Typography sx={{ ...subtitleSx, marginTop: '20px' }}>
            Connect your Microsoft account to use in FluxPrompt
          </Typography>
          <Button
            variant="text"
            disabled={!installUrl || isConnecting}
            sx={{
              color: 'rgba(54, 80, 222, 1)',
              fontSize: '12px',
              lineHeight: '14px',
              fontWeight: 400,
              marginTop: '10px',
            }}
            onClick={connectionHandler}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </Box>
      );
    }

    if (
      connectionState === ConnectionState.JustConnected ||
      connectionState === ConnectionState.Connected
    ) {
      return (
        <Box sx={cardSx}>
          <Box sx={{ borderBottom: '.5px solid rgba(234, 236, 240, 1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <MicrosoftIcon />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography sx={titleSx}>Microsoft Account</Typography>
                <Typography sx={subtitleSx}>{connectedEmail}</Typography>
              </Box>
              <Box
                sx={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={() => setShowLogoutBox(s => !s)}
              >
                <MoreIcon />
                {renderLogoutBox()}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '5px',
            }}
          >
            <Connected />
            <Typography
              sx={{
                ...subtitleSx,
                fontSize: '12px',
                lineHeight: '14px',
                marginTop: '5px',
              }}
            >
              {connectionState === ConnectionState.JustConnected
                ? 'You are successfully logged in to your Microsoft account.'
                : 'You are currently connected to Microsoft.'}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (connectionState === ConnectionState.AccessTimedOut) {
      return (
        <Box sx={cardSx}>
          <Box sx={{ borderBottom: '.5px solid rgba(234, 236, 240, 1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <MicrosoftIcon />
              <Typography sx={titleSx}>Microsoft Account</Typography>
              <Box
                sx={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={() => setShowLogoutBox(s => !s)}
              >
                <MoreIcon />
                {renderLogoutBox()}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AccessTimedOutIcon />
              <Typography sx={{ ...subtitleSx, marginTop: '20px' }}>
                Access timed out. Reconnect to continue using Microsoft.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="text"
            disabled={!installUrl || isConnecting}
            sx={{
              color: 'rgba(54, 80, 222, 1)',
              fontSize: '12px',
              lineHeight: '14px',
              fontWeight: 400,
              marginTop: '10px',
            }}
            onClick={connectionHandler}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
          </Button>
        </Box>
      );
    }

    return null;
  };

  return (
    <>
      {showLogoutModal && (
        <LogoutModal
          onHide={() => setShowLogoutModal(false)}
          logoutHandler={logoutHandler}
        />
      )}
      {renderConnectionState()}
    </>
  );
};

export default MicrosoftIntegration;
